'use client';

import Link from 'next/link';
import type { ComponentProps, MouseEvent } from 'react';

import { captureBrowserEvent } from '@/lib/analytics/client-capture';
import type { BrowserEvents } from '@/lib/analytics/events';
import { sanitizeHref } from '@/lib/analytics/sanitize';

type MarketingCtaLinkProps = Omit<ComponentProps<typeof Link>, 'href'> & {
  href: string;
  location: BrowserEvents['marketing_cta_clicked']['location'];
  linkType: BrowserEvents['marketing_cta_clicked']['link_type'];
};

export default function MarketingCtaLink({
  location,
  linkType,
  href,
  onClick,
  className,
  children,
  target,
  rel,
  ...props
}: MarketingCtaLinkProps) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    captureBrowserEvent('marketing_cta_clicked', {
      location,
      link_type: linkType,
      href: sanitizeHref(href),
    });
    onClick?.(event);
  };

  if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    const staysOnThisPage = href.startsWith('mailto:') || href.startsWith('tel:');

    return (
      <a
        href={href}
        className={className}
        target={staysOnThisPage ? undefined : target}
        rel={staysOnThisPage ? undefined : rel}
        onClick={handleClick}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      {...props}
      href={href}
      className={className}
      target={target}
      rel={rel}
      onClick={handleClick}
    >
      {children}
    </Link>
  );
}
